import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const GoogleCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setIsAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleGoogleCallback = async () => {
      try {
        // Parse URL parameters
        const urlParams = new URLSearchParams(location.search);
        const accessToken = urlParams.get('access_token');
        const refreshToken = urlParams.get('refresh_token');
        const code = urlParams.get('code');
        const state = urlParams.get('state');

        if (accessToken && refreshToken) {
          // Direct token callback from backend
          localStorage.setItem('access_token', accessToken);
          localStorage.setItem('refresh_token', refreshToken);

          // Fetch user profile with the new token
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/profile`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const userData = await response.json();
            
            // Store user data
            localStorage.setItem('user', JSON.stringify(userData));
            setUser(userData);
            setIsAuthenticated(true);

            // Redirect to home page
            navigate('/', { replace: true });
          } else {
            throw new Error('Failed to fetch user profile');
          }
        } else if (code && state) {
          // OAuth code callback - need to exchange with backend
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/auth/google/exchange`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ code, state }),
          });

          if (response.ok) {
            const data = await response.json();
            if (data.access_token && data.refresh_token) {
              localStorage.setItem('access_token', data.access_token);
              localStorage.setItem('refresh_token', data.refresh_token);
              
              // Store user data if provided
              if (data.user) {
                localStorage.setItem('user', JSON.stringify(data.user));
                setUser(data.user);
              }
              
              setIsAuthenticated(true);
              navigate('/', { replace: true });
            } else {
              throw new Error('Backend response missing tokens');
            }
          } else {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Authentication failed');
          }
        } else {
          throw new Error('Missing authentication parameters in callback URL');
        }
      } catch (err) {
        console.error('Google OAuth callback error:', err);
        setError(err.message || 'Authentication failed');
        setLoading(false);
        
        // Redirect to home after a delay
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
      }
    };

    handleGoogleCallback();
  }, [location, navigate, setUser, setIsAuthenticated]);

  if (loading && !error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #667eea',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '20px'
        }}></div>
        <h2>Completing Google Sign In...</h2>
        <p>Please wait while we set up your account.</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: 'Arial, sans-serif',
        textAlign: 'center',
        padding: '20px'
      }}>
        <div style={{
          fontSize: '48px',
          color: '#e74c3c',
          marginBottom: '20px'
        }}>⚠️</div>
        <h2 style={{ color: '#e74c3c', marginBottom: '10px' }}>Authentication Error</h2>
        <p style={{ color: '#666', marginBottom: '20px' }}>{error}</p>
        <p style={{ color: '#999', fontSize: '14px' }}>Redirecting to home page...</p>
      </div>
    );
  }

  return null;
};

export default GoogleCallback;